import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchBar } from "./SearchBar";
import { PropertyMap } from "./PropertyMap";
import { PropertyCard } from "./PropertyCard";
import { Property } from "@/types/property";
import { Button } from "./ui/button";
import { LayoutGrid, Map as MapIcon, ChevronLeft, ChevronRight, Building2, X } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Filters, FilterState } from "./Filters";
import { cn, formatPrice } from "@/lib/utils";
import axiosInstance from '../config/axios';
import { GridFSImage } from "./GridFSImage";
import { toast } from "sonner";
import L from 'leaflet';

const PROPERTIES_PER_PAGE = 9;

export function PropertiesCatalog() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [properties, setProperties] = useState<Property[]>([]);
  const [filteredProperties, setFilteredProperties] = useState<Property[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedOperationType, setSelectedOperationType] = useState<string>("todos");
  const [selectedPropertyType, setSelectedPropertyType] = useState<string>("todos");
  const [selectedCountry, setSelectedCountry] = useState<string>("todos");
  const [selectedCity, setSelectedCity] = useState<string>("todos");
  const [filters, setFilters] = useState<FilterState>({
    type: 'all',
    propertyType: 'all',
    country: 'all',
    city: 'todos',
    minPrice: undefined,
    maxPrice: undefined,
    beds: undefined,
    baths: undefined,
    superficie: undefined,
    estacionamientos: undefined,
    antiguedad: undefined,
    caracteristicas: [],
    publicadoHoy: false,
    tiendaOficial: false,
    visitaOnline: false,
    priceRange: undefined
  });
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const [highlightedProperty, setHighlightedProperty] = useState<string | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const operationTypes = [
    { value: "todos", label: "Todos" },
    { value: "venta", label: "Venta" },
    { value: "arriendo", label: "Alquiler" },
    { value: "temporal", label: "Alquiler Temporal" }
  ];

  const propertyTypes = [
    { value: "todos", label: "Todos" },
    { value: "casa", label: "Casa" },
    { value: "apartamento", label: "Apartamento" },
    { value: "departamento", label: "Departamento" },
    { value: "terreno", label: "Terreno" },
    { value: "local", label: "Local" },
    { value: "oficina", label: "Oficina" },
    { value: "casa_comercial", label: "Casa Comercial" },
    { value: "industrial", label: "Industrial" },
    { value: "parcelacion", label: "Parcelación" }
  ];

  const countries = [
    { value: "todos", label: "Todos" },
    { value: "venezuela", label: "Venezuela" },
    { value: "colombia", label: "Colombia" },
    { value: "peru", label: "Perú" },
    { value: "chile", label: "Chile" },
    { value: "argentina", label: "Argentina" },
    { value: "mexico", label: "México" },
    { value: "brasil", label: "Brasil" }
  ];

  const fetchProperties = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get('/api/properties');
      const propertiesData = response.data.properties || [];
      
      setProperties(propertiesData);
      setFilteredProperties(propertiesData);
      setIsLoading(false);
    } catch (error: any) {
      console.error('Error fetching properties:', error);
      toast.error('Error al cargar las propiedades.');
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProperties();
  }, []);

  useEffect(() => {
    if (!isLoading) {
      handleSearch();
    }
  }, [selectedOperationType, selectedPropertyType, selectedCountry, selectedCity, filters, isLoading]);

  const handleSearch = () => {
    const filtered = filterProperties(properties);
    setFilteredProperties(filtered);
    setCurrentPage(1); // Reset to first page on new search
  };

  const filterProperties = (properties: Property[]) => {
    return properties.filter(property => {
      // Operation Type Filter
      const matchesOperationType = selectedOperationType === "todos" || 
        (selectedOperationType === "venta" && (property.type === "sale" || property.operationType === "sale")) ||
        (selectedOperationType === "arriendo" && (property.type === "rent" || property.operationType === "rent")) ||
        (selectedOperationType === "temporal" && (property.type === "temporal" || property.operationType === "temporal"));
      
      if (!matchesOperationType) return false;
      
      // Property Type Filter
      const propertyTypeValue = property.propertyType?.toLowerCase() || '';
      const matchesPropertyType = selectedPropertyType === "todos" || 
        propertyTypeValue === selectedPropertyType.toLowerCase();
      
      if (!matchesPropertyType) return false;
      
      // Country Filter (Simplified for now)
      if (selectedCountry !== "todos") {
        const propertyCountry = property.location?.country?.toLowerCase() || '';
        if (!propertyCountry.includes(selectedCountry.toLowerCase())) return false;
      }

      // City Filter
      if (selectedCity !== "todos") {
        const propertyCity = property.location?.city?.toLowerCase() || '';
        if (!propertyCity.includes(selectedCity.toLowerCase())) return false;
      }

      // Advanced Filters
      if (filters.minPrice && property.price < filters.minPrice) return false;
      if (filters.maxPrice && property.price > filters.maxPrice) return false;
      if (filters.beds && property.bedrooms < filters.beds) return false;
      if (filters.baths && property.bathrooms < filters.baths) return false;

      return true;
    });
  };

  const handleFilterChange = (name: keyof FilterState, value: any) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const totalPages = Math.ceil(filteredProperties.length / PROPERTIES_PER_PAGE);
  const currentGridProperties = filteredProperties.slice((currentPage - 1) * PROPERTIES_PER_PAGE, currentPage * PROPERTIES_PER_PAGE);

  if (isLoading) return (
    <div className="py-20 text-center animate-pulse">
      <Building2 className="h-12 w-12 mx-auto text-terracotta mb-4" />
      <p className="font-display font-black uppercase tracking-widest text-ink">Cargando Propiedades...</p>
    </div>
  );

  return (
    <div className="space-y-12">
      {/* Search and Filters Bar */}
      <div className="bg-ink py-6 sticky top-20 z-40 border-b-2 border-terracotta -mx-4 px-4 sm:mx-0">
        <div className="container mx-auto">
          <div className="flex flex-wrap items-center justify-between gap-6">
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex flex-col">
                <span className="text-[9px] uppercase tracking-widest text-terracotta font-black mb-1">Operación</span>
                <Select value={selectedOperationType} onValueChange={setSelectedOperationType}>
                  <SelectTrigger className="w-[150px] bg-transparent text-parchment border-none p-0 h-auto rounded-none uppercase text-xs font-black tracking-[0.2em] focus:ring-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-ink text-parchment border-ink brutalist-border-sm">
                    {operationTypes.map((t) => <SelectItem key={t.value} value={t.value} className="focus:bg-terracotta uppercase text-xs font-black">{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col">
                <span className="text-[9px] uppercase tracking-widest text-terracotta font-black mb-1">Tipo</span>
                <Select value={selectedPropertyType} onValueChange={setSelectedPropertyType}>
                  <SelectTrigger className="w-[150px] bg-transparent text-parchment border-none p-0 h-auto rounded-none uppercase text-xs font-black tracking-[0.2em] focus:ring-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-ink text-parchment border-ink brutalist-border-sm">
                    {propertyTypes.map((t) => <SelectItem key={t.value} value={t.value} className="focus:bg-terracotta uppercase text-xs font-black">{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col">
                <span className="text-[9px] uppercase tracking-widest text-terracotta font-black mb-1">País</span>
                <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                  <SelectTrigger className="w-[150px] bg-transparent text-parchment border-none p-0 h-auto rounded-none uppercase text-xs font-black tracking-[0.2em] focus:ring-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-ink text-parchment border-ink brutalist-border-sm">
                    {countries.map((c) => <SelectItem key={c.value} value={c.value} className="focus:bg-terracotta uppercase text-xs font-black">{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <Filters filters={filters} onFilterChange={handleFilterChange} />
              <div className="h-8 w-[1px] bg-parchment/10" />
              <div className="flex bg-parchment/10 p-1 brutalist-border-sm border-parchment/20">
                <Button 
                  variant={viewMode === 'list' ? 'default' : 'ghost'} 
                  size="sm" 
                  onClick={() => setViewMode('list')}
                  className={cn("h-8 px-3 rounded-none text-[10px] font-black", viewMode === 'list' ? "bg-terracotta" : "text-parchment")}
                >
                  GRID
                </Button>
                <Button 
                  variant={viewMode === 'map' ? 'default' : 'ghost'} 
                  size="sm" 
                  onClick={() => setViewMode('map')}
                  className={cn("h-8 px-3 rounded-none text-[10px] font-black", viewMode === 'map' ? "bg-terracotta" : "text-parchment")}
                >
                  MAPA
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 pb-20">
        {viewMode === 'list' ? (
          <>
            {currentGridProperties.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 mt-12">
                {currentGridProperties.map((property) => (
                  <PropertyCard
                    key={property._id || property.id}
                    {...property}
                  />
                ))}
              </div>
            ) : (
              <div className="py-40 text-center brutalist-border bg-ink text-parchment relative overflow-hidden mt-12">
                <div className="absolute inset-0 opacity-10 grain-texture" />
                <Building2 className="h-24 w-24 mx-auto text-terracotta mb-8 animate-pulse" />
                <h3 className="text-4xl font-display font-black uppercase mb-4">No se encontraron propiedades</h3>
                <p className="text-xl font-serif italic text-parchment/60 mb-12 max-w-lg mx-auto">
                  No hay propiedades que coincidan con sus criterios actuales. Intente ajustar los filtros.
                </p>
                <Button 
                  onClick={() => {
                    setSelectedOperationType("todos");
                    setSelectedPropertyType("todos");
                    setSelectedCountry("todos");
                    setSelectedCity("todos");
                    setFilters({
                      type: 'all',
                      propertyType: 'all',
                      country: 'all',
                      city: 'todos',
                      caracteristicas: [],
                      publicadoHoy: false,
                      tiendaOficial: false,
                      visitaOnline: false
                    });
                  }}
                  variant="terracotta"
                  size="lg"
                >
                  REINICIAR FILTROS
                </Button>
              </div>
            )}

            {totalPages > 1 && (
              <div className="flex justify-between items-center border-t-2 border-ink pt-12 mt-20">
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="gap-4"
                >
                  <ChevronLeft className="h-5 w-5" /> ANTERIOR
                </Button>
                <span className="text-2xl font-display font-black">{currentPage} / {totalPages}</span>
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="gap-4"
                >
                  SIGUIENTE <ChevronRight className="h-5 w-5" />
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="h-[700px] brutalist-border bg-ink mt-8 relative">
            <PropertyMap 
              properties={filteredProperties}
              onMapLoad={(map) => { mapRef.current = map; }}
              highlightedProperty={highlightedProperty}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default PropertiesCatalog;
